/**
 * KontrollePage — Administration › Kontrollfunktion (Berechtigungskonzept V4.0 · Bereich 5,
 * Kapitel 7): the per-broker Fristen config for the Kontrollbereich age-stages.
 *
 * Four rows (Stufe 1-4): the stage name + a `tage_schwelle` number input + the seeded default
 * hint. Monotonic-validated St1 < St2 < St3 < St4 (client pre-check; the api re-validates and is
 * the hard line). The change requires an Administrationsrolle and is NOT retroactive.
 *
 * NOTE: the read-only cross-customer Rückstand list itself moved OUT of Administration onto the
 * Portfolio Dashboard (the Kontrollbereich Cockpit panel + /portfolio/kontrolle routes) — this
 * admin surface is now purely the threshold configuration.
 *
 * Wire: GET + PUT /admin/einstellungen/kontrolle-fristen.
 * [Source: apps/web/src/features/administration/pages/ProzessErinnerungenPage.tsx] (config GET/PUT form)
 */

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { cn } from '../../../lib/cn';
import { AdminPageHeader } from '../AdminPageHeader';
import { ADMIN_PAGE_META } from '../adminNav';
import { Button, LoadingState, ErrorState, TextInput } from '../components';
import { useKontrolleFristen } from '../../control/hooks';
import { STUFE_CLASSES, STUFE_NAME, STUFE_DEFAULT_TAGE } from '../../control/constants';
import type { KontrolleStufe } from '../../control/types';

const meta = ADMIN_PAGE_META.kontrolle;

const STUFEN: KontrolleStufe[] = [1, 2, 3, 4];

export function KontrollePage() {
  const { data, isLoading, isError } = useKontrolleFristen();
  const qc = useQueryClient();

  // One text value per stage, hydrated from the server config.
  const [values, setValues] = useState<Record<KontrolleStufe, string>>({
    1: String(STUFE_DEFAULT_TAGE[1]),
    2: String(STUFE_DEFAULT_TAGE[2]),
    3: String(STUFE_DEFAULT_TAGE[3]),
    4: String(STUFE_DEFAULT_TAGE[4]),
  });
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data == null) return;
    setValues((prev) => {
      const next = { ...prev };
      for (const s of data.stufen) next[s.stufe] = String(s.tage_schwelle);
      return next;
    });
  }, [data]);

  const nums = STUFEN.map((s) => Number(values[s]));
  // Monotonic St1 < St2 < St3 < St4 (strictly increasing) + whole numbers ≥ 1.
  const allInts = nums.every((n) => Number.isInteger(n) && n >= 1);
  const monotonic = nums[0]! < nums[1]! && nums[1]! < nums[2]! && nums[2]! < nums[3]!;
  const valid = allInts && monotonic;

  const save = useMutation({
    mutationFn: () => {
      if (!allInts) throw new Error('Bitte ganze Zahlen ab 1 Tag eingeben.');
      if (!monotonic) {
        throw new Error('Die Fristen müssen aufsteigend sein: Stufe 1 < Stufe 2 < Stufe 3 < Stufe 4.');
      }
      return apiClient.admin.setKontrolleFristen({
        stufen: STUFEN.map((s) => ({ stufe: s, tage_schwelle: Number(values[s]) })),
      });
    },
    onSuccess: () => {
      setError(null);
      setSaved(true);
      void qc.invalidateQueries({ queryKey: qk.kontrolleFristen() });
      void qc.invalidateQueries({ queryKey: qk.kontrolleUebersicht() });
    },
    onError: (e: Error) => {
      setSaved(false);
      setError(e.message);
    },
  });

  return (
    <div>
      <AdminPageHeader title={meta.title} description={meta.description} />

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState />
      ) : (
        <div className="max-w-xl space-y-6">
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="space-y-3">
              {STUFEN.map((s) => {
                const c = STUFE_CLASSES[s];
                return (
                  <div key={s} className="flex items-center gap-3">
                    <span className={cn('size-2.5 shrink-0 rounded-full', c.dot)} aria-hidden="true" />
                    <div className="w-40 shrink-0">
                      <div className="text-sm font-medium text-foreground">
                        Stufe {s}: {STUFE_NAME[s]}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Default: {STUFE_DEFAULT_TAGE[s]} Tage
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <TextInput
                        inputMode="numeric"
                        aria-label={`Stufe ${s} Tage-Schwelle`}
                        value={values[s]}
                        onChange={(e) => {
                          setSaved(false);
                          setValues((prev) => ({ ...prev, [s]: e.target.value }));
                        }}
                        className="w-24 tabular-nums"
                      />
                      <span className="text-sm text-muted-foreground">Tage</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="mt-4 text-xs text-muted-foreground">
              Die Änderung erfordert eine Administrationsrolle und wirkt nicht rückwirkend.
            </p>
          </div>

          {!valid && (
            <div className="text-sm text-status-danger">
              Die Fristen müssen aufsteigend sein: Stufe 1 &lt; Stufe 2 &lt; Stufe 3 &lt; Stufe 4.
            </div>
          )}
          {error != null && <div className="text-sm text-status-danger">{error}</div>}
          {saved && error == null && <div className="text-sm text-status-success">Gespeichert.</div>}

          <div className="flex justify-end">
            <Button size="sm" disabled={!valid || save.isPending} onClick={() => save.mutate()}>
              Speichern
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
